import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useWorkload (SOUND-538 / backend SOUND-537) — the resource/workload aggregate.
 * Internal-only endpoint (`GET /workload`); pass `enabled: false` to skip the
 * call on non-internal surfaces (avoids a guaranteed 403). Thin makeApiRequest
 * wrapper (house pattern; not React Query), auth + CompanyHeader from
 * useAuth()/useEffectiveCompanyId().
 *
 * Response: { data: { byFunction: [...], byAssignee: [...], unassigned: {...} } }
 *
 * @returns {{workload: object|null, loading: boolean, error: any, refetch: Function}}
 */
export function useWorkload({ enabled = true } = {}) {
  const [workload, setWorkload] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchWorkload = useCallback(async () => {
    if (!enabled || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest("get", apiRoutes.getWorkload, auth.user, companyId);
      setWorkload(res.data?.data || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [enabled, companyId, auth?.user]);

  useEffect(() => {
    fetchWorkload();
  }, [fetchWorkload]);

  return { workload, loading, error, refetch: fetchWorkload };
}
